import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PencilIcon, TrashIcon, PlusIcon, DocumentTextIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useSettings } from '../../contexts/SettingsContext';
import type { LegalDocument } from '../../types/settings';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

const legalDocumentSchema = z.object({
  type: z.string().min(1, 'Type is required'),
  title: z.string().min(1, 'Title is required'),
  content: z.string().min(1, 'Content is required'),
  version: z.string().min(1, 'Version is required'),
  is_active: z.boolean().default(true),
  effective_date: z.string().min(1, 'Effective date is required')
});

type LegalDocumentFormData = z.infer<typeof legalDocumentSchema>;

export default function LegalDocumentsForm() {
  const { state, updateLegalDocument, addLegalDocument, deleteLegalDocument } = useSettings();
  const [editingDocument, setEditingDocument] = useState<LegalDocument | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<LegalDocument | null>(null);
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm<LegalDocumentFormData>({
    resolver: zodResolver(legalDocumentSchema),
    defaultValues: {
      type: '',
      title: '',
      content: '',
      version: '1.0',
      is_active: true,
      effective_date: new Date().toISOString().split('T')[0]
    }
  });

  const handleEdit = (document: LegalDocument) => {
    setEditingDocument(document);
    reset({
      type: document.type,
      title: document.title,
      content: document.content,
      version: document.version,
      is_active: document.is_active,
      effective_date: document.effective_date
    });
    setIsAdding(false);
    setPreviewDocument(null);
  };

  const handleAdd = () => {
    setEditingDocument(null);
    reset({
      type: '',
      title: '',
      content: '',
      version: '1.0',
      is_active: true,
      effective_date: new Date().toISOString().split('T')[0]
    });
    setIsAdding(true);
    setPreviewDocument(null);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this legal document?')) {
      try {
        await deleteLegalDocument(id);
      } catch (error) {
        console.error('Error deleting legal document:', error);
      }
    }
  };

  const handlePreview = (document: LegalDocument) => {
    setPreviewDocument(document);
    setEditingDocument(null);
    setIsAdding(false);
  };

  const onSubmit = async (data: LegalDocumentFormData) => {
    try {
      if (editingDocument) {
        await updateLegalDocument({
          ...editingDocument,
          type: data.type,
          title: data.title,
          content: data.content,
          version: data.version,
          is_active: data.is_active,
          effective_date: data.effective_date
        });
      } else {
        await addLegalDocument({
          type: data.type,
          title: data.title,
          content: data.content,
          version: data.version,
          is_active: data.is_active,
          effective_date: data.effective_date
        });
      }
      
      setEditingDocument(null);
      setIsAdding(false);
      reset();
    } catch (error) {
      console.error('Error saving legal document:', error);
    }
  };

  const cancelEdit = () => {
    setEditingDocument(null);
    setIsAdding(false);
    reset();
  };

  const closePreview = () => {
    setPreviewDocument(null);
  };

  // Group documents by type
  const groupedDocuments = state.legalDocuments.reduce((acc, document) => {
    if (!acc[document.type]) {
      acc[document.type] = [];
    }
    acc[document.type].push(document);
    return acc;
  }, {} as Record<string, LegalDocument[]>);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-golf-gray">Legal Documents</h2>
        <button
          onClick={handleAdd}
          className="flex items-center space-x-2 px-4 py-2 bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          <span>Add Document</span>
        </button>
      </div>

      {previewDocument && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-golf-gray">{previewDocument.title}</h3>
            <button
              onClick={closePreview}
              className="text-gray-500 hover:text-gray-700"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="mb-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Version: {previewDocument.version}
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              Effective: {new Date(previewDocument.effective_date).toLocaleDateString()}
            </span>
            {previewDocument.is_active ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Active
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                Inactive
              </span>
            )}
          </div>
          <div className="prose max-w-none">
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked(previewDocument.content) as string) }} />
          </div>
        </div>
      )}

      {(isAdding || editingDocument) && !previewDocument && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-golf-gray mb-4">
            {editingDocument ? 'Edit Legal Document' : 'Add New Legal Document'}
          </h3>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  {...register('type')}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                >
                  <option value="">Select a type</option>
                  <option value="privacy_policy">Privacy Policy</option>
                  <option value="terms_of_service">Terms of Service</option>
                  <option value="cookie_policy">Cookie Policy</option>
                  <option value="disclaimer">Disclaimer</option>
                  <option value="refund_policy">Refund Policy</option>
                  <option value="other">Other</option>
                </select>
                {errors.type && (
                  <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('title')}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Version <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('version')}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                  placeholder="e.g., 1.0, 2.1"
                />
                {errors.version && (
                  <p className="mt-1 text-sm text-red-600">{errors.version.message}</p>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  {...register('effective_date')}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                />
                {errors.effective_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.effective_date.message}</p>
                )}
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  {...register('is_active')}
                  className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                  Active document
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Content <span className="text-red-500">*</span>
              </label>
              <textarea
                {...register('content')}
                rows={15}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange font-mono text-sm"
                placeholder="Enter your document content in Markdown format"
              />
              {errors.content && (
                <p className="mt-1 text-sm text-red-600">{errors.content.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Use Markdown format for content. Headers, lists, links, and other formatting are supported.
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={cancelEdit}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90"
              >
                {editingDocument ? 'Update Document' : 'Add Document'}
              </button>
            </div>
          </form>
        </div>
      )}

      {!previewDocument && !isAdding && !editingDocument && (
        <>
          {state.loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
            </div>
          ) : state.error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {state.error}
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedDocuments).map(([type, documents]) => (
                <div key={type} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-golf-gray capitalize">
                      {type.replace(/_/g, ' ')}
                    </h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {documents.map((document) => (
                      <div key={document.id} className="px-6 py-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center">
                              <h4 className="text-md font-medium text-golf-gray">{document.title}</h4>
                              {document.is_active ? (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Active
                                </span>
                              ) : (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  Inactive
                                </span>
                              )}
                            </div>
                            <div className="flex items-center mt-1 text-sm text-gray-500">
                              <span>Version: {document.version}</span>
                              <span className="mx-2">•</span>
                              <span>Effective: {new Date(document.effective_date).toLocaleDateString()}</span>
                            </div>
                            <div className="mt-2">
                              <button
                                onClick={() => handlePreview(document)}
                                className="text-sm text-golf-orange hover:text-golf-green flex items-center"
                              >
                                <DocumentTextIcon className="w-4 h-4 mr-1" />
                                Preview Document
                              </button>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEdit(document)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <PencilIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(document.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {Object.keys(groupedDocuments).length === 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                  <p className="text-gray-500">No legal documents found. Add your first document using the button above.</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}